import {
  ClusterStatusChip,
  K8sProviderIcon,
  ClusterStatusTooltip,
} from '@cast/features-cluster-ui';
import round from 'lodash/round';

import { CAST_AI } from '@cast/constants';
import { Button, Typography, Box, Stack, tokens } from '@cast/design-system';
import { isDemoCluster, nonNegativeGuard } from '@cast/utils';

import { ClusterDemoStatusChip } from 'components/chips';
import { useAvailableSavingsQuery } from 'hooks/queries/available-savings';
import { useEnableCastAiButtonState } from 'hooks/useEnableCastAiButtonState';
import { ContentContainer } from 'main-layout/ContentContainer';

import {
  useCluster,
  isAKSCluster,
  isKOPSCluster,
  isClusterOnboardingPhase2,
} from '@cast/shared-cluster-context-data-access';

export const EnableOptimizationHeader = () => {
  const { cluster, isManaged, isKentManaged, isKentEligible } = useCluster();
  const { isLoading: isLoadingAvailableSavings, data } =
    useAvailableSavingsQuery(cluster.id, !isManaged);

  const enableCastAiState = useEnableCastAiButtonState();

  const hasLoaded = !!data;
  const availableSavingsPercentage = Math.max(
    nonNegativeGuard(data?.recommendations?.Layman?.savingsPercentage),
    nonNegativeGuard(data?.recommendations?.SpotInstances?.savingsPercentage)
  );
  const isClusterOptimal = hasLoaded && availableSavingsPercentage === 0;
  const isKops = isKOPSCluster(cluster);
  const isAks = isAKSCluster(cluster);
  const clusterIsConnecting = isClusterOnboardingPhase2(
    cluster.status,
    isManaged
  );

  if (
    !cluster ||
    enableCastAiState.hasBeenEnabled ||
    !enableCastAiState.canBeEnabled ||
    // Don’t show CAST autoscaler related UI for KENT-managed and eligible clusters, since scaling is handled by Karpenter and we don’t support transitioning between the two
    isKentManaged ||
    isKentEligible
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        borderStyle: 'solid',
        borderWidth: '1px 0',
        borderColor: tokens.theme.border.structural.neutral,
        backgroundColor: '#FFFFFF', // DS grey 100
      }}
      data-testid="enable-optimization-header"
    >
      <ContentContainer height="49px">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="24px"
          height="100%"
        >
          <Stack direction="row" alignItems="center" gap="24px">
            <Stack direction="row" gap="4px" alignItems="center">
              <K8sProviderIcon size={20} provider={cluster.providerType} />
              <Typography variant="A1">{cluster.name}</Typography>
            </Stack>

            {isDemoCluster(cluster.id) ? (
              <ClusterDemoStatusChip />
            ) : (
              <ClusterStatusTooltip cluster={cluster}>
                <ClusterStatusChip status={cluster.displayStatus} />
              </ClusterStatusTooltip>
            )}
          </Stack>
          {isAks && clusterIsConnecting && (
            <Typography variant="P12M" color="grey.600">
              Enabling access. It might take up to 15 minutes...
            </Typography>
          )}
          {isKops && !isManaged && (
            <Typography variant="P12M" color="grey.600">
              Only “Read-only” features are available for kOps
            </Typography>
          )}
          {enableCastAiState.canBeEnabled && (
            <Stack direction="row" gap="24px" alignItems="center">
              {!isLoadingAvailableSavings && (
                <Stack direction="row" gap="8px" alignItems="baseline">
                  {isClusterOptimal ? (
                    <Typography variant="L12R">Cluster is optimal</Typography>
                  ) : (
                    <>
                      <Typography variant="L12R">Save</Typography>
                      <Typography variant="h6">
                        {hasLoaded
                          ? `${round(availableSavingsPercentage, 1)}%`
                          : '--'}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
              {enableCastAiState.hasPermissionsToEnable && (
                <Button
                  data-testid="open-phase-2-dialog-button"
                  aria-label="open assign credentials modal button"
                  size="sm"
                  onClick={enableCastAiState.enable}
                  sx={{ typography: 'P12B' }}
                >
                  Enable {CAST_AI}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </ContentContainer>
    </Box>
  );
};
